/**
 * ContractsOverview — Vue globale contrats de travail (Vague 5).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Clock, FileText } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { cdi: "CDI", cdd: "CDD", alternance: "Alternance", stage: "Stage", interim: "Intérim" };

export default function ContractsOverview() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ alert_end_soon: 0, alert_probation_soon: 0 });
  useEffect(() => { api.get("/hr/contracts-overview").then(r => {
    setRows(r.data.contracts || []);
    setStats({ alert_end_soon: r.data.alert_end_soon, alert_probation_soon: r.data.alert_probation_soon });
  }).catch(e => toast.error(apiError(e))); }, []);
  return (
    <div className="space-y-6" data-testid="contracts-overview-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Contrats de travail
        </h1>
        <p className="text-sm text-slate-500">
          {rows.length} contrats
          {stats.alert_end_soon > 0 && <span className="text-red-600 ml-2">· {stats.alert_end_soon} {"échéance <30j"}</span>}
          {stats.alert_probation_soon > 0 && <span className="text-amber-600 ml-2">· {stats.alert_probation_soon} {"fin essai <30j"}</span>}
        </p>
      </div>
      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-12 text-center"><FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucun contrat.</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">Salarié</TableHead>
                <TableHead className="text-xs uppercase">Type</TableHead>
                <TableHead className="text-xs uppercase">Début</TableHead>
                <TableHead className="text-xs uppercase">Fin</TableHead>
                <TableHead className="text-xs uppercase">Fin essai</TableHead>
                <TableHead className="text-xs uppercase text-right">Salaire annuel</TableHead>
                <TableHead className="text-xs uppercase">Alertes</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((c, i) => (
                  <TableRow key={c.id} className="border-b hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/app/hr/employees/${c.employee_id}`)}
                    data-testid={`ctr-row-${i}`}>
                    <TableCell>
                      <div className="font-medium text-sm">{c.employee_name}</div>
                      <div className="text-xs text-slate-500">{c.job_title || "—"}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {TYPE_LABEL[c.contract_type] || c.contract_type}</Badge></TableCell>
                    <TableCell className="font-mono text-sm">{c.start_date && new Date(c.start_date).toLocaleDateString("fr-FR")}</TableCell>
                    <TableCell className="font-mono text-sm">{c.end_date ? new Date(c.end_date).toLocaleDateString("fr-FR") : "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{c.probation_end_date ? new Date(c.probation_end_date).toLocaleDateString("fr-FR") : "—"}</TableCell>
                    <TableCell className="text-right font-mono">
                      {c.gross_annual_salary ? `${Number(c.gross_annual_salary).toLocaleString("fr-FR")} €` : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {c.alert_end_soon && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />Fin</Badge>}
                        {c.alert_probation_soon && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"><Clock className="w-3 h-3 mr-0.5" />Essai</Badge>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
